import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { BACKEND_URL } from '../config/canonical';
import { useAuth } from '../context/AuthContext';
import {
  NxButton, NxPanel, NxChip, NxAlertStrip,
} from '../components/nx';
import {
  RefreshCw,
  FolderArchive, BellRing, MessageSquare, FilePlus2,
  Search, Shield, ShieldCheck, Landmark, Building2, GraduationCap, StickyNote,
  FileSignature,
} from 'lucide-react';
import DashboardCaseMap from '../components/DashboardCaseMap';
import AdminTimeClockTiles from '../components/admin/AdminTimeClockTiles';

export default function Dashboard() {
  const { user, isAdmin } = useAuth();
  // Iter131g — browser push registration moved to AuthContext so it
  // fires for every role right after login, not just when the user
  // visits /dashboard.
  const navigate = useNavigate();
  const token = typeof window !== 'undefined' ? localStorage.getItem('nexus_token') : null;

  const [stats, setStats] = useState(null);
  const [investigators, setInvestigators] = useState([]);
  const [acceptance, setAcceptance] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [s, inv, ca] = await Promise.all([
        fetch(`${BACKEND_URL}/api/dashboard/stats`,                  { headers }).then(r => r.ok ? r.json() : null).catch(() => null),
        fetch(`${BACKEND_URL}/api/investigators/tracking`,           { headers }).then(r => r.ok ? r.json() : []).catch(() => []),
        fetch(`${BACKEND_URL}/api/case-acceptance/dashboard-stats`,  { headers }).then(r => r.ok ? r.json() : null).catch(() => null),
      ]);
      setStats(s);
      setInvestigators(Array.isArray(inv) ? inv : (inv?.investigators || []));
      setAcceptance(ca);
    } catch (e) { /* surfaced via empty states */ }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const onDuty = investigators.filter(i => ['on_duty', 'active', 'available', 'on_call'].includes((i.status || '').toLowerCase()));

  const complianceAlerts = acceptance ? [
    acceptance.pending && { type: 'PENDING_ACCEPTANCE', label: 'Case Acceptance pending', count: acceptance.pending, link: '/case-acceptance' },
    acceptance.expired && { type: 'EXPIRED', label: 'Expired acceptance forms', count: acceptance.expired, link: '/case-acceptance' },
  ].filter(Boolean) : [];

  return (
    <div className="nx" data-testid="dashboard">
      {/* ============ COMMAND-CENTER WELCOME HEADER ============ */}
      <CommandHeader user={user} onDuty={onDuty} fetchAll={fetchAll} />

      {/* ============ TOP QUICK-ACTION TILES (5 colored) ============ */}
      <TopQuickActionTiles navigate={navigate} isAdmin={isAdmin} />

      {/* ============ ADMIN · TIME CLOCK TILES (admin only) ============ */}
      <AdminTimeClockTiles />

      {/* ============ 3-COLUMN: MAP · ANALYTICS · NOTIFICATIONS ============ */}
      <div
        data-testid="dashboard-three-col"
        style={{ display: 'grid', gap: 14, gridTemplateColumns: 'minmax(0, 1.35fr) minmax(0, 1fr) minmax(0, 0.85fr)', marginBottom: 14 }}
        className="nx-dash-3col"
      >
        <DashboardCaseMap />
        <CaseAnalyticsCard stats={stats} navigate={navigate} />
        <RecentNotificationsCard navigate={navigate} />
      </div>

      {/* ============ RECENT CASES TABLE ============ */}
      <RecentCasesCard navigate={navigate} />

      {/* ============ ALERT RIBBON (only when relevant) ============ */}
      {complianceAlerts.length > 0 && (
        <div className="mt-4">
          <NxAlertStrip
            tone="amber"
            title={`${complianceAlerts.reduce((a, b) => a + (b.count || 0), 0)} compliance items need attention`}
            body={complianceAlerts.map(a => `${a.label}: ${a.count}`).join(' · ')}
            action={
              <NxButton
                variant="secondary"
                className="nx-btn--sm"
                style={{ background: 'rgba(0,0,0,0.15)', borderColor: 'rgba(0,0,0,0.20)', color: '#1A1300' }}
                onClick={() => navigate('/case-acceptance')}
              >
                Review
              </NxButton>
            }
            data-testid="dashboard-compliance-alert"
          />
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------
// TOP QUICK-ACTION TILES — big colored tiles matching the mockup.
// ---------------------------------------------------------------------
function TopQuickActionTiles({ navigate, isAdmin }) {
  const allTiles = [
    { label: 'New Case',        sub: 'Create a new investigation', icon: 'plus',    bg: 'linear-gradient(135deg, #2563EB 0%, #1E40AF 100%)', path: '/cases?create=1', testid: 'qa-tile-new-case', adminOnly: true },
    { label: 'New Client',      sub: 'Add a client to the system', icon: 'user',    bg: 'linear-gradient(135deg, #10B981 0%, #047857 100%)', path: '/clients?create=1', testid: 'qa-tile-new-client', adminOnly: true },
    { label: 'Assign Case',     sub: 'Assign case to investigator',icon: 'assign',  bg: 'linear-gradient(135deg, #8B5CF6 0%, #6D28D9 100%)', path: '/cases', testid: 'qa-tile-assign-case' },
    { label: 'Training Center', sub: 'Access training courses',    icon: 'cap',     bg: 'linear-gradient(135deg, #F59E0B 0%, #B45309 100%)', path: '/training-center', testid: 'qa-tile-training-center' },
    { label: 'New Report',      sub: 'Generate a case report',     icon: 'report',  bg: 'linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)', path: '/reports/create', testid: 'qa-tile-new-report' },
  ];
  const tiles = allTiles.filter(t => !t.adminOnly || isAdmin);
  const colCount = tiles.length;
  const iconSvg = {
    plus:  <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" width="26" height="26"><path d="M12 5v14M5 12h14" strokeLinecap="round" /></svg>,
    user:  <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" width="26" height="26"><circle cx="12" cy="8" r="4" /><path d="M4 21c1.5-4 5-6 8-6s6.5 2 8 6" strokeLinecap="round" /></svg>,
    assign:<svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" width="26" height="26"><circle cx="9" cy="8" r="3.5" /><circle cx="17" cy="9" r="2.5" /><path d="M3 20c1.2-3.2 3.8-5 6-5s4.8 1.8 6 5" strokeLinecap="round" /></svg>,
    cap:   <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" width="26" height="26"><path d="M12 4l10 5-10 5-10-5 10-5z" strokeLinejoin="round" /><path d="M6 11v5c0 1.5 3 3 6 3s6-1.5 6-3v-5" strokeLinecap="round" /></svg>,
    report:<svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" width="26" height="26"><rect x="5" y="3" width="14" height="18" rx="2" /><path d="M9 8h6M9 12h6M9 16h4" strokeLinecap="round" /></svg>,
  };
  return (
    <div style={{ display: 'grid', gap: 12, gridTemplateColumns: `repeat(${colCount}, 1fr)`, marginBottom: 14 }} className="nx-quick-tiles" data-testid="dashboard-top-tiles">
      {tiles.map(t => (
        <button key={t.label} type="button" onClick={() => navigate(t.path)} data-testid={t.testid}
          style={{
            display: 'flex', alignItems: 'center', gap: 12, padding: '16px 18px',
            borderRadius: 14, border: 'none', color: 'white', textAlign: 'left', cursor: 'pointer',
            background: t.bg, boxShadow: '0 4px 14px rgba(0,0,0,0.35)',
            transition: 'transform 0.15s ease',
          }}
          onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-2px)'}
          onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
        >
          <div style={{ width: 44, height: 44, borderRadius: '50%', background: 'rgba(255,255,255,0.20)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            {iconSvg[t.icon]}
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 700, lineHeight: 1.1 }}>{t.label}</div>
            <div style={{ fontSize: 11, opacity: 0.85, marginTop: 3 }}>{t.sub}</div>
          </div>
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------
// CASE ANALYTICS CARD — 4 KPI stats + donut + horizontal bars.
// ---------------------------------------------------------------------
function CaseAnalyticsCard({ stats, navigate }) {
  const kpis = [
    { label: 'Active Cases', value: stats?.active_cases ?? stats?.total_cases ?? 0, delta: '+8%', tone: '#10B981' },
    { label: 'New Leads',    value: stats?.total_leads ?? 0,                       delta: '+33%', tone: '#10B981' },
    { label: 'Closed Cases', value: stats?.closed_cases ?? 0,                       delta: '+14%', tone: '#10B981' },
    { label: 'Pending',      value: stats?.pending_cases ?? 0,                     delta: '-17%', tone: '#EF4444' },
  ];
  const byType = stats?.cases_by_type || {};
  const typeRows = Object.entries(byType).sort((a,b) => (b[1]||0)-(a[1]||0)).slice(0, 5);
  const maxType = Math.max(1, ...typeRows.map(r => r[1] || 0));
  const total = stats?.total_cases || (stats?.active_cases||0) + (stats?.closed_cases||0) + (stats?.pending_cases||0) || 0;
  const active = stats?.active_cases || 0;
  const closed = stats?.closed_cases || 0;
  const pending = Math.max(0, total - active - closed);
  const pctActive = total ? Math.round(active/total*100) : 0;
  const pctClosed = total ? Math.round(closed/total*100) : 0;
  const pctPending = total ? 100 - pctActive - pctClosed : 0;
  return (
    <div className="nx-panel" data-testid="dashboard-analytics" style={{ padding: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <div style={{ width: 26, height: 26, borderRadius: 6, background: '#1E3A8A', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#60A5FA', fontWeight: 800 }}>◧</div>
        <div style={{ fontSize: 15, fontWeight: 700, color: '#F8FAFC' }}>Case Analytics</div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 12 }}>
        {kpis.map(k => (
          <div key={k.label} style={{ background: '#0F1B2D', border: '1px solid #1E293B', borderRadius: 8, padding: '10px 8px' }} data-testid={`analytics-kpi-${k.label.toLowerCase().replace(/\s+/g,'-')}`}>
            <div style={{ fontSize: 22, fontWeight: 800, color: '#F8FAFC', lineHeight: 1 }}>{k.value}</div>
            <div style={{ fontSize: 9, color: '#94A3B8', letterSpacing: '0.08em', textTransform: 'uppercase', marginTop: 4 }}>{k.label}</div>
            <div style={{ fontSize: 10, color: k.tone, marginTop: 3, fontWeight: 600 }}>{k.delta}</div>
            <div style={{ fontSize: 8, color: '#64748B' }}>vs. last 30 days</div>
          </div>
        ))}
      </div>
      <div style={{ fontSize: 11, color: '#94A3B8', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 8, fontWeight: 700 }}>Case Status Breakdown</div>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 14 }}>
        <svg viewBox="0 0 42 42" width="100" height="100">
          <circle cx="21" cy="21" r="15.9" fill="transparent" stroke="#1E293B" strokeWidth="6" />
          <circle cx="21" cy="21" r="15.9" fill="transparent" stroke="#3B82F6" strokeWidth="6" strokeDasharray={`${pctActive} ${100-pctActive}`} strokeDashoffset="25" />
          <circle cx="21" cy="21" r="15.9" fill="transparent" stroke="#10B981" strokeWidth="6" strokeDasharray={`${pctClosed} ${100-pctClosed}`} strokeDashoffset={`${25 - pctActive}`} />
          <circle cx="21" cy="21" r="15.9" fill="transparent" stroke="#F59E0B" strokeWidth="6" strokeDasharray={`${pctPending} ${100-pctPending}`} strokeDashoffset={`${25 - pctActive - pctClosed}`} />
          <text x="21" y="21" textAnchor="middle" dominantBaseline="central" fill="#F8FAFC" fontSize="7" fontWeight="700">{total}</text>
          <text x="21" y="26" textAnchor="middle" dominantBaseline="central" fill="#94A3B8" fontSize="3">Total Cases</text>
        </svg>
        <div style={{ flex: 1, fontSize: 12, display: 'grid', gap: 5 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', color: '#F8FAFC' }}>
            <span><span style={{ display: 'inline-block', width: 8, height: 8, background: '#3B82F6', borderRadius: '50%', marginRight: 6 }} />Active</span>
            <span>{active} <span style={{ color: '#94A3B8', marginLeft: 6 }}>{pctActive}%</span></span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', color: '#F8FAFC' }}>
            <span><span style={{ display: 'inline-block', width: 8, height: 8, background: '#10B981', borderRadius: '50%', marginRight: 6 }} />Closed</span>
            <span>{closed} <span style={{ color: '#94A3B8', marginLeft: 6 }}>{pctClosed}%</span></span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', color: '#F8FAFC' }}>
            <span><span style={{ display: 'inline-block', width: 8, height: 8, background: '#F59E0B', borderRadius: '50%', marginRight: 6 }} />Pending</span>
            <span>{pending} <span style={{ color: '#94A3B8', marginLeft: 6 }}>{pctPending}%</span></span>
          </div>
        </div>
      </div>
      {typeRows.length > 0 && (<>
        <div style={{ fontSize: 11, color: '#94A3B8', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 6, fontWeight: 700 }}>Cases by Type</div>
        {typeRows.map(([k, v]) => (
          <div key={k} style={{ display: 'grid', gridTemplateColumns: '110px 1fr 20px', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <div style={{ fontSize: 11, color: '#CBD5E1', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{k.replace(/_/g, ' ').toLowerCase()}</div>
            <div style={{ background: '#1E293B', height: 8, borderRadius: 4, overflow: 'hidden' }}>
              <div style={{ background: '#3B82F6', height: '100%', width: `${Math.round((v/maxType)*100)}%` }} />
            </div>
            <div style={{ fontSize: 11, color: '#F8FAFC', textAlign: 'right', fontWeight: 600 }}>{v}</div>
          </div>
        ))}
      </>)}
    </div>
  );
}

// ---------------------------------------------------------------------
// RECENT NOTIFICATIONS CARD (with compact Quick Actions box beneath)
// ---------------------------------------------------------------------
const NOTIF_QUICK_ACTIONS = [
  { label: 'My Documents',    icon: FileSignature, path: '/client-documents' },
  { label: 'Messages',        icon: MessageSquare, path: '/messages' },
  { label: 'File Report',     icon: FilePlus2,     path: '/reports/create' },
  { label: 'Case Search',     icon: Search,        path: '/cases' },
  { label: 'Training',        icon: GraduationCap, path: '/training-center' },
];
function RecentNotificationsCard({ navigate }) {
  const token = typeof window !== 'undefined' ? localStorage.getItem('nexus_token') : null;
  const [items, setItems] = React.useState([]);
  React.useEffect(() => {
    if (!token) return;
    fetch(`${BACKEND_URL}/api/notifications?limit=6`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : []).then(d => setItems(Array.isArray(d) ? d : (d?.notifications || []))).catch(() => {});
  }, [token]);
  const dotColor = (t) => ({
    NEW_CASE: '#EF4444', CLIENT_UPDATE: '#10B981', LEAD: '#F59E0B',
    REPORT: '#3B82F6', MESSAGE: '#8B5CF6',
  }[String(t || '').toUpperCase()] || '#F59E0B');
  return (
    <div className="nx-panel" data-testid="dashboard-notifications" style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <BellRing size={16} style={{ color: '#F59E0B' }} />
          <div style={{ fontSize: 15, fontWeight: 700, color: '#F8FAFC' }}>Recent Notifications</div>
        </div>
        <button type="button" onClick={() => navigate('/notifications')}
          style={{ background: 'transparent', border: 'none', color: '#60A5FA', fontSize: 12, cursor: 'pointer' }}
          data-testid="notif-view-all">View All</button>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 260, overflowY: 'auto' }}>
        {items.length === 0 && <div style={{ fontSize: 11, color: '#64748B', padding: '10px 0' }}>No recent notifications.</div>}
        {items.slice(0, 6).map((n, i) => (
          <div key={n.id || i} style={{ display: 'flex', gap: 8, padding: 8, background: '#0F1B2D', borderRadius: 8, border: '1px solid #1E293B' }} data-testid={`notif-item-${i}`}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: dotColor(n.type), marginTop: 6, flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, color: '#F8FAFC', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{n.title || n.message || 'Notification'}</div>
              <div style={{ fontSize: 10, color: '#94A3B8', marginTop: 2 }}>{n.subtitle || n.description || ''}</div>
            </div>
            <div style={{ fontSize: 10, color: '#64748B', flexShrink: 0 }}>
              {n.created_at ? new Date(n.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
            </div>
          </div>
        ))}
      </div>

      {/* Iter124 — compact Quick Actions box beneath the Notifications */}
      <div style={{ marginTop: 4, padding: 10, background: '#0F1B2D', border: '1px solid #1E293B', borderRadius: 10 }} data-testid="notif-quick-actions">
        <div style={{ fontSize: 10, color: '#94A3B8', letterSpacing: '0.10em', textTransform: 'uppercase', marginBottom: 8, fontWeight: 700 }}>Quick Actions</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 }}>
          {NOTIF_QUICK_ACTIONS.map(({ label, icon: Icon, path }) => (
            <button key={label} type="button" onClick={() => navigate(path)}
              style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
                padding: '10px 6px', border: '1px solid #1E293B', borderRadius: 8,
                background: '#131A2A', color: '#CBD5E1', cursor: 'pointer', fontSize: 10,
              }}
              data-testid={`notif-qa-${label.toLowerCase().replace(/\s+/g,'-')}`}
            >
              <Icon size={16} style={{ color: '#F59E0B' }} />
              <span style={{ textAlign: 'center', lineHeight: 1.15 }}>{label}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------
// RECENT CASES CARD — table with case #, client, type, county, status.
// ---------------------------------------------------------------------
function RecentCasesCard({ navigate }) {
  const token = typeof window !== 'undefined' ? localStorage.getItem('nexus_token') : null;
  const [rows, setRows] = React.useState([]);
  React.useEffect(() => {
    if (!token) return;
    fetch(`${BACKEND_URL}/api/cases?limit=6`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : []).then(d => setRows(Array.isArray(d) ? d.slice(0, 6) : [])).catch(() => {});
  }, [token]);
  const statusBadge = (s) => {
    const map = { ACTIVE: '#10B981', PENDING: '#F59E0B', PENDING_REVIEW: '#F59E0B', CLOSED: '#64748B', NEW: '#3B82F6', COMPLETED: '#22D3EE' };
    return map[String(s || '').toUpperCase()] || '#64748B';
  };
  return (
    <div className="nx-panel" data-testid="dashboard-recent-cases" style={{ padding: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <FolderArchive size={16} style={{ color: '#F59E0B' }} />
          <div style={{ fontSize: 15, fontWeight: 700, color: '#F8FAFC' }}>Recent Cases</div>
        </div>
        <button type="button" onClick={() => navigate('/cases')}
          style={{ background: 'transparent', border: 'none', color: '#60A5FA', fontSize: 12, cursor: 'pointer' }}
          data-testid="recent-cases-view-all">View All</button>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ color: '#94A3B8', textAlign: 'left', borderBottom: '1px solid #1E293B' }}>
              <th style={{ padding: '8px 10px', fontWeight: 700, fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>Case #</th>
              <th style={{ padding: '8px 10px', fontWeight: 700, fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>Case Name</th>
              <th style={{ padding: '8px 10px', fontWeight: 700, fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>Type</th>
              <th className="nx-hide-mobile" style={{ padding: '8px 10px', fontWeight: 700, fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>County</th>
              <th style={{ padding: '8px 10px', fontWeight: 700, fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>Status</th>
              <th className="nx-hide-mobile" style={{ padding: '8px 10px', fontWeight: 700, fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>Last Update</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={6} style={{ padding: 14, color: '#64748B', textAlign: 'center', fontSize: 11 }}>No cases yet.</td></tr>
            )}
            {rows.map(c => (
              <tr key={c.id} onClick={() => navigate(`/cases/${c.id}`)} style={{ borderBottom: '1px solid #1E293B', cursor: 'pointer' }}
                data-testid={`recent-case-${c.id}`}>
                <td style={{ padding: '10px', color: '#F8FAFC', fontFamily: 'JetBrains Mono, monospace', fontSize: 11, whiteSpace: 'nowrap' }}>{c.case_number || c.id}</td>
                <td style={{ padding: '10px', color: '#F8FAFC', whiteSpace: 'nowrap', fontWeight: 600 }}>{c.title || c.case_title || (c.case_type || '').replace(/_/g, ' ') || '—'}</td>
                <td style={{ padding: '10px', color: '#CBD5E1', whiteSpace: 'nowrap' }}>{(c.case_type || '').replace(/_/g, ' ')}</td>
                <td className="nx-hide-mobile" style={{ padding: '10px', color: '#CBD5E1', whiteSpace: 'nowrap' }}>{c.county || '—'}</td>
                <td style={{ padding: '10px', whiteSpace: 'nowrap' }}>
                  <span style={{ display: 'inline-block', padding: '3px 8px', borderRadius: 999, fontSize: 10, fontWeight: 700, background: `${statusBadge(c.status)}22`, color: statusBadge(c.status), whiteSpace: 'nowrap' }}>
                    {(c.status || '').replace(/_/g, ' ')}
                  </span>
                </td>
                <td className="nx-hide-mobile" style={{ padding: '10px', color: '#94A3B8', fontSize: 11, whiteSpace: 'nowrap' }}>{c.last_activity_at ? new Date(c.last_activity_at).toLocaleString() : ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------
// COMMAND-CENTER WELCOME HEADER
// ---------------------------------------------------------------------
function CommandHeader({ user, onDuty, fetchAll }) {
  const [now, setNow] = React.useState(new Date());
  React.useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(t);
  }, []);
  const h = now.getHours();
  const greeting = h < 12 ? 'Good Morning' : h < 17 ? 'Good Afternoon' : 'Good Evening';
  const firstName = user?.name?.split(' ')[0] || 'Operator';
  const roleTitle = (user?.title || user?.role || 'Operator').replace(/_/g, ' ');
  const agency = user?.agency_name || user?.company_name || 'Nexus Investigation Agency';
  const statusTone = onDuty.length > 0 ? 'lime' : 'amber';
  const statusLabel = onDuty.length > 0 ? 'Operational' : 'Standby';
  return (
    <div
      className="nx-panel mb-4"
      data-testid="dashboard-banner"
      style={{ padding: 0, overflow: 'hidden', position: 'relative', borderRadius: 14, minHeight: 210 }}
    >
      <img
        src="/nexus-hero-dallas-eagle.jpg"
        alt="Dallas skyline"
        loading="lazy"
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center center' }}
      />
      <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(90deg, rgba(15,27,45,0.88) 0%, rgba(15,27,45,0.55) 45%, rgba(15,27,45,0.05) 100%)' }} />
      <div style={{ position: 'relative', padding: '20px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16 }} className="nx-cmd-header-body">
        <div style={{ minWidth: 0, flex: 1 }} className="nx-cmd-header-left">
          <div className="nx-eyebrow" data-testid="dash-greeting">{greeting}</div>
          <h1 className="nx-h1" data-testid="dashboard-title" style={{ fontSize: 'clamp(1.5rem, 3vw, 2rem)', textShadow: '0 4px 18px rgba(0,0,0,0.55)' }}>
            {firstName}
          </h1>
          <p style={{ color: '#E2E8F0', fontWeight: 600, fontSize: 13, margin: '2px 0', textShadow: '0 2px 8px rgba(0,0,0,0.5)' }}>
            <span style={{ textTransform: 'capitalize' }}>{roleTitle}</span> · {agency}
          </p>
          <div className="flex flex-wrap items-center gap-2 mt-2" style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11, color: '#94A3B8', letterSpacing: '0.10em' }}>
            <span>{now.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }).toUpperCase()}</span>
            <span style={{ opacity: 0.4 }}>·</span>
            <span data-testid="dash-clock">{now.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}</span>
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }} className="nx-cmd-header-right">
          <NxChip tone={statusTone} data-testid="dash-status">{statusLabel}</NxChip>
          <NxButton variant="ghost" className="nx-btn--sm" onClick={fetchAll}
            style={{ background: 'rgba(15,27,45,0.55)', color: 'white', backdropFilter: 'blur(4px)' }}>
            <RefreshCw size={13} /> Refresh
          </NxButton>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------
// LIVE OPERATIONS STATS — single source of truth.
// Every count comes from /api/dashboard/stats so it matches the
// counts visible on /cases, /reports, /case-logs, /evidence, /team etc.
// ---------------------------------------------------------------------
function LiveOperationsStats({ stats, onDuty, navigate }) {
  const cards = [
    { label: 'Assigned Cases',        value: stats?.total_cases ?? 0,         path: '/cases' },
    { label: 'Reports',               value: stats?.total_reports ?? 0,       path: '/reports' },
    { label: 'Leads',                 value: stats?.total_leads ?? 0,         path: '/cases' },
    { label: 'Field Logs',            value: stats?.total_field_logs ?? 0,    path: '/case-logs' },
    { label: 'Subjects',              value: stats?.total_subjects ?? 0,      path: '/cases' },
    { label: 'Evidence Items',        value: stats?.total_evidence ?? 0,      path: '/evidence' },
    { label: 'Investigators',         value: stats?.total_investigators ?? 0, path: '/team' },
    { label: 'Active Surveillance',   value: stats?.active_surveillance ?? 0, path: '/cases?status=ACTIVE' },
    { label: 'Open Tasks',            value: stats?.open_tasks ?? 0,          path: '/cases' },
    { label: 'Overdue',               value: stats?.overdue_tasks ?? 0,       path: '/cases?status=OVERDUE', tone: 'red' },
  ];
  return (
    <div
      className="nx-ops-stats"
      data-testid="dashboard-ops-stats"
      style={{ display: 'grid', gap: 8, gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', marginBottom: 18 }}
    >
      {cards.map((c) => (
        <button
          key={c.label}
          type="button"
          onClick={() => navigate(c.path)}
          className="nx-panel"
          data-testid={`ops-stat-${c.label.toLowerCase().replace(/\s+/g, '-')}`}
          style={{
            padding: '12px 14px', textAlign: 'left', cursor: 'pointer',
            background: c.tone === 'red' ? 'linear-gradient(180deg, rgba(248,113,113,0.06) 0%, rgba(248,113,113,0.02) 100%)' : 'var(--nx-surface)',
            border: '1px solid var(--nx-border-soft)',
          }}
        >
          <div style={{ fontSize: 26, fontWeight: 800, color: c.tone === 'red' ? '#FCA5A5' : '#F8FAFC', lineHeight: 1, fontFamily: 'var(--nx-font-display)' }}>
            {String(c.value).padStart(2, '0')}
          </div>
          <div style={{ fontSize: 10, fontFamily: 'JetBrains Mono, monospace', color: '#94A3B8', letterSpacing: '0.10em', textTransform: 'uppercase', marginTop: 4 }}>
            {c.label}
          </div>
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------
// QUICK ACTION ICON CENTER — Sheriff-style: small icon + label beneath.
// ---------------------------------------------------------------------
const QUICK_ACTIONS = [
  { label: 'My Documents',     icon: FileSignature, path: '/client-documents' },
  { label: 'Notifications',    icon: BellRing,      path: '/notifications' },
  { label: 'Messages',         icon: MessageSquare, path: '/messages' },
  { label: 'File Report',      icon: FilePlus2,     path: '/reports/create' },
  { label: 'Case Search',      icon: Search,        path: '/cases' },
  { label: 'Police Directory', icon: Shield,        path: '/directories?tab=police' },
  { label: 'Sheriffs',         icon: ShieldCheck,   path: '/directories?tab=sheriff' },
  { label: 'Courts',           icon: Landmark,      path: '/directories?tab=court' },
  { label: 'Jails',            icon: Building2,     path: '/directories?tab=jail' },
  { label: 'Training',         icon: GraduationCap, path: '/training-center' },
  { label: 'Quick Notes',      icon: StickyNote,    path: '/quick-notes' },
];
function QuickActionIcons({ user, navigate }) {
  return (
    <NxPanel title="Quick Actions" subtitle="ONE-TAP COMMAND TOOLS" style={{ marginBottom: 18 }} data-testid="dashboard-quick-actions">
      <div
        style={{ display: 'grid', gap: 10, gridTemplateColumns: 'repeat(auto-fill, minmax(96px, 1fr))' }}
        data-testid="quick-actions-grid"
      >
        {QUICK_ACTIONS.map(({ label, icon: Icon, path }) => (
          <button
            key={label}
            type="button"
            onClick={() => navigate(path)}
            className="nx-quick-action"
            data-testid={`qa-${label.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <div className="nx-quick-action-icon"><Icon size={22} strokeWidth={1.6} /></div>
            <div className="nx-quick-action-label">{label}</div>
          </button>
        ))}
      </div>
    </NxPanel>
  );
}
